import React, { useCallback, useContext, useEffect, useState } from "react";
import Accordion from "../components/Accordion";
import { Box, Divider, Grid } from "@mui/material";
import CampaignSetupButton from "../components/CampaignSetup/CampaignSetupButton";
import { COLOR_MAP } from "../../../constants/ColorNames";
import LeftSection from "../components/CampaignCompose/LeftSection/LeftSection";
import MiddleSection from "../components/CampaignCompose/MiddleSection/MiddleSection";
import RightSection from "../components/CampaignCompose/RightSection/RightSection";
import { useDispatch, useSelector } from "react-redux";
import {
  campaignSenderDetailsSelector,
  mediaDetailsSelector,
  templateDetailsSelector,
  templatePlaceholdersCacheSelector,
  templatePlaceholdersSelector,
} from "../../../redux/selectors/campaignCompose.selector";
import {
  campaignComposeActions,
  defaultSenderDetails,
} from "../../../redux/slice/campaignComposeSlice";
import {
  ICampaignSaveAsDraftPayload,
  IErrors,
  IMediaDetails,
  ITemplate,
  ITemplatePlaceholderValues,
} from "../../../types/campaign.types";
import {
  faqDetailsSelector,
  saveAndContinueResponseSelector,
} from "../../../redux/selectors/whatsAppCampaign.selector";
import {
  useGetTemplatesPlaceholdersMutation,
  usePostStartCampaignMutation,
} from "../../../redux/services/campaignComposeService";
import { campaignSetupDetailsSelector } from "../../../redux/selectors/campaignSetup.selector";
import { ToastContext } from "../../../utils/context/toastContext";
import { usePostSaveAsDraftMutation } from "../../../redux/services/campaignSetupService";
import { MIDDLEWARE_SLUG } from "../../../apis/apiEndPoints";
import { getDisabledFields, getFormData } from "../../../utils/helpers";
import { getErrorMsg, getIsFresher } from "../../../utils/whatsAppUtils";
import useDialogState from "../../../hooks/useDialogState";
import PlaceholdersDialog from "../components/CampaignCompose/PlaceholdersDialog";
import { eventPushFunction } from "@foundit/clevertap";
import { RootState } from "../../../redux/store";
import { usePostEventTrackingMutation } from "../../../redux/services/eventTracking";
interface IProps {
  isEditMode: boolean;
  stepsCompleted: number;
  stepNumber: number;
  handleEditClick: (step: number) => void;
  initiationId?: string;
  campaignId?: string;
}

const validationErrors = {
  addMedia: "Kindly upload a media.",
};

const WhatsAppCompose: React.FC<IProps> = ({
  stepsCompleted,
  isEditMode,
  stepNumber,
  handleEditClick,
  initiationId,
  campaignId,
}) => {
  const [errors, setErrors] = useState<IErrors>({});
  const { setToastValue } = useContext(ToastContext);

  const dispatch = useDispatch();
  const { templateDetails } = useSelector(templateDetailsSelector);
  const { mediaDetails } = useSelector(mediaDetailsSelector);
  const { faqDetails } = useSelector(faqDetailsSelector);
  const { setUpDetails } = useSelector(campaignSetupDetailsSelector);
  const { senderDetails } = useSelector(campaignSenderDetailsSelector);
  const { templatePlaceholders } = useSelector(templatePlaceholdersSelector);
  const { saveAndContinueResponse } = useSelector(
    saveAndContinueResponseSelector
  );
  const { templatePlaceholdersCache } = useSelector(
    templatePlaceholdersCacheSelector
  );
  const userDetails = useSelector(
    (state: RootState) => state.campaign.userDetails
  );

  const campaignCreationQueryData = useSelector((state: RootState) => state.campaign.campaignCreationQueryData);
  const userData = useSelector((state: RootState) => state.campaign.userData);
  const candidateNo = useSelector((state: RootState) => state.campaign.candidateNo);
  const srpDisabledFields = useSelector((state: RootState) => state.campaign.disabledFields)
  const urlParams = new URLSearchParams(window.location.search);
  let startTime = 0

  const {
    open: openPlaceholdersDialog,
    handleClose: handleClosePlaceholdersDialog,
    handleOpen: handleOpenPlaceholdersDialog,
  } = useDialogState();

  const [
    startCampaignApi,
    {
      isError: isStartCampaignError,
      isSuccess: isStartCampaignSuccess,
      error: startCampaignError,
    },
  ] = usePostStartCampaignMutation();
  const [
    saveAsDraftApi,
    {
      isError: isSaveAsDraftError,
      isSuccess: isSaveAsDraftSuccess,
      error: saveAsDraftError,
    },
  ] = usePostSaveAsDraftMutation();

  const [
    getPlaceholdersApi,
    {
      data: placeholders,
      status: getPlaceholdersStatus,
      error: getPlaceholdersError,
    },
  ] = useGetTemplatesPlaceholdersMutation();

  const [postEvent] = usePostEventTrackingMutation();

  const handleUpdateMediaDetails = useCallback(
    (mediaDetails: IMediaDetails) => {
      dispatch(campaignComposeActions.setMediaDetails({ mediaDetails }));
    },
    [dispatch]
  );

  const handleUpdateSelectedTemplate = useCallback(
    (newSelectedTemplate: ITemplate | null) => {
      if (!newSelectedTemplate) {
        dispatch(
          campaignComposeActions.setSelectedTemplate({
            selectedTemplate: null,
          })
        );
        return;
      }

      // if cache available
      const templateCache = templatePlaceholdersCache[newSelectedTemplate?.id];
      if (templateCache && getPlaceholdersStatus !== "rejected") {
        dispatch(
          campaignComposeActions.setSelectedTemplate({
            selectedTemplate: templateCache.template,
          })
        );

        dispatch(
          campaignComposeActions.setTemplatePlaceholders({
            placeholders: templateCache.placeholders,
          })
        );
      } else {
        dispatch(
          campaignComposeActions.setSelectedTemplate({
            selectedTemplate: newSelectedTemplate,
          })
        );

        if (initiationId && (campaignId || saveAndContinueResponse?.id)) {
          getPlaceholdersApi({
            initiationId,
            campaignId: campaignId || saveAndContinueResponse?.id || "",
            templateId: newSelectedTemplate.id,
          });
        }
      }
    },
    [
      campaignId,
      dispatch,
      getPlaceholdersApi,
      getPlaceholdersStatus,
      initiationId,
      saveAndContinueResponse?.id,
      templatePlaceholdersCache,
    ]
  );

  const handleErrors = useCallback(
    (newErrors: IErrors) => {
      const errorsMap: IErrors = { ...errors, ...newErrors };
      setErrors(errorsMap);
    },
    [errors]
  );

  const getCampaignId = useCallback(() => {
    if (campaignId) {
      return +campaignId;
    }
    if (saveAndContinueResponse?.id) {
      return +saveAndContinueResponse.id;
    }

    return null;
  }, [campaignId, saveAndContinueResponse?.id]);

  const handleStartCampaign = useCallback(() => {
     startTime = (Date.now()) / 1000;
    const {
      campaignName,
      experienceRange,
      salaryRange,
      jobMode,
      isSendToAlreadyContacted,
      selectedFolder,
      numberOfSelectedCandidates,
      jobTitle,
      jobLocations,
      noticePeriod,
    } = setUpDetails;

    // validation
    const errorsMap: IErrors = {};
    if (mediaDetails?.addMediaFlag && !mediaDetails?.uploadedFile) {
      errorsMap["addMedia"] = validationErrors.addMedia;
    }
    // check optional placeholders are available
    const optionalPlaceholders =
      templateDetails?.selectedTemplate?.optionalPlaceholders || [];

    const shouldShowDialog = optionalPlaceholders.some((placeholder) => {
      if (placeholder === "@min_salary" || placeholder === "@max_salary") {
        return !salaryRange?.currency || !salaryRange?.min || !salaryRange?.max;
      }
      if (placeholder === "@notice_period") {
        return !noticePeriod;
      }
      return false;
    });

    if (shouldShowDialog) {
      handleOpenPlaceholdersDialog();
      return;
    }

    if (Object.keys(errorsMap)?.length) {
      setErrors(errorsMap);
    } else {
      const payload: ICampaignSaveAsDraftPayload = {
        campaignName,
        folderId: selectedFolder?.folderId || -1,
        folderName: selectedFolder?.name || "",
        initiationId: initiationId ? +initiationId : -1,
        jobDescription: {
          jobTitle,
          currencyCode: salaryRange.currency,
          jobLocations:
            jobMode === "Remote"
              ? []
              : jobLocations?.map((item) => item.name) || [],
          jobMode,
          maxExp: getIsFresher(experienceRange.min)
            ? "1 year"
            : `${experienceRange.max}`,
          minExp: `${experienceRange.min}`,
          maxSalary: salaryRange.max,
          minSalary: salaryRange.min,
          noticePeriod: noticePeriod || "",
        },
        recruiterDetail: null,
        selectedCount: +numberOfSelectedCandidates,
        sendMailToContactedProfiles: isSendToAlreadyContacted ? 1 : 0,
        status: "SCHEDULED",
        campaignId: getCampaignId(),
        templateId: templateDetails.selectedTemplate?.id,
        whatsAppName: senderDetails?.name,
        whatsAppNumber: senderDetails?.number,
        whatsAppCountryCode: senderDetails?.countryCode,
        whatsAppNumberId: senderDetails?.id || "",
      };

      const formData = getFormData(payload);
      if (mediaDetails.uploadedFile) {
        formData.append("attachment", mediaDetails.uploadedFile);
      }

      startCampaignApi({
        payload: formData,
        isMedia: !!mediaDetails?.addMediaFlag,
      }).unwrap()
      .then((res) => {
        let endTime = (Date.now()) / 1000
        if (campaignCreationQueryData?.searchId && campaignCreationQueryData?.childSearchId) {
                    let eventData = {
                    ACTION_NAME: 'send_whatsapp',
                    API_RESPONSE_CODE: 200,
                    API_RESPONSE_TIME: Date.now(),
                    PAGE_LOAD_STATUS: "SUCCESS",
                    PAGE_LOAD_TIME: endTime - startTime,
                    API_REQUEST_TIME: startTime * 1000,
                    SEARCH_ID: campaignCreationQueryData.searchId,
                    CHILD_SEARCH_ID: campaignCreationQueryData.childSearchId,
                    PROFILES_SELECTED: +numberOfSelectedCandidates,
                    PAGE_TYPE: campaignCreationQueryData.triggeredFromPage,
                    SUB_UID: userData.recruiterId,
                    // "SEARCH_PAGE_VNO": "v4.1",
                    PAGE_URL: window.location.href,
                    XCODE: userDetails.x_code,
                    SESSION_ID: userData.trackingId,
                    SITECONTEXT: userData.siteContext,
                    LANDING_TIMESTAMP: "",
                    CLICK_TIME: Date.now(),
                    // DB_VIEW: All,
                    EVENT_CREATED_TIME: Date.now(),
                    BULK:false
                  }
                  postEvent(eventData)
                  }
      });
    }
  }, [
    setUpDetails,
    mediaDetails?.addMediaFlag,
    mediaDetails.uploadedFile,
    templateDetails.selectedTemplate?.optionalPlaceholders,
    templateDetails.selectedTemplate?.id,
    handleOpenPlaceholdersDialog,
    initiationId,
    getCampaignId,
    senderDetails?.name,
    senderDetails?.number,
    senderDetails?.countryCode,
    senderDetails?.id,
    startCampaignApi,
  ]);

  const handleSaveAsDraft = useCallback(() => {
    const {
      campaignName,
      experienceRange,
      salaryRange,
      jobMode,
      isSendToAlreadyContacted,
      selectedFolder,
      numberOfSelectedCandidates,
      jobTitle,
      jobLocations,
      noticePeriod,
    } = setUpDetails;
    // save as draft
    const disabledFields = getDisabledFields(srpDisabledFields)
    const payload: ICampaignSaveAsDraftPayload = {
      campaignName,
      folderId: selectedFolder?.folderId || null,
      folderName: selectedFolder?.name || "",
      initiationId: initiationId ? +initiationId : null,
      jobDescription: {
        jobTitle,
        currencyCode: salaryRange.currency,
        jobLocations:
          jobMode === "Remote"
            ? []
            : jobLocations?.map((item) => item.name) || [],
        jobMode,
        maxExp: getIsFresher(experienceRange.min)
          ? "1 year"
          : `${experienceRange.max}`,
        minExp: `${experienceRange.min}`,
        maxSalary: salaryRange.max,
        minSalary: salaryRange.min,
        noticePeriod: noticePeriod || "",
        disabledFields,
        channelName:urlParams.get('cn')
      },
      recruiterDetail: null,
      selectedCount: +numberOfSelectedCandidates,
      sendMailToContactedProfiles: isSendToAlreadyContacted ? 1 : 0,
      status: "DRAFT",
      campaignId: getCampaignId(),
      templateId: templateDetails.selectedTemplate?.id,
      whatsAppName: senderDetails?.name,
      whatsAppNumber: senderDetails?.number,
      whatsAppCountryCode: senderDetails?.countryCode,
      whatsAppNumberId: senderDetails?.id || "",
    };

    const formData = getFormData(payload);

    saveAsDraftApi({ payload: formData });
  }, [
    setUpDetails,
    initiationId,
    getCampaignId,
    templateDetails.selectedTemplate?.id,
    senderDetails?.name,
    senderDetails?.number,
    senderDetails?.countryCode,
    senderDetails?.id,
    saveAsDraftApi,
  ]);

  useEffect(() => {
    // to handle start campaign
    if (isStartCampaignSuccess) {
      // clever tap event on start campaign
      eventPushFunction({
        eventName: "Start_campaign_clicked",
        data: {
          xcode: userDetails?.x_code || "",
          subuid: userDetails?.recruiter_id || "",
          channel_id: userDetails?.channel_id || "",
          subchannel_id: userDetails?.subhchannel_id || "",
          outreach_type: "WhatsApp",
          step: 2,
          campaign_id: saveAndContinueResponse?.id || "",
          selected_template: templateDetails.selectedTemplate?.id || "",
          media_type: mediaDetails?.mediaType || "",
        },
      });

      window.location.href = `${window.location.protocol}//${window.location.host}${MIDDLEWARE_SLUG}/listing`;
    }

    if (isStartCampaignError) {
      setToastValue({
        isToastShown: true,
        toastMessage: getErrorMsg({
          error: startCampaignError,
          defaultMsg: "Failed to start campaign. Please ty again.",
        }),
        toastKey: "saveAndContinueError",
        toastSeverity: "error",
      });
    }
  }, [
    isStartCampaignError,
    isStartCampaignSuccess,
    mediaDetails?.mediaType,
    saveAndContinueResponse?.id,
    setToastValue,
    startCampaignError,
    templateDetails.selectedTemplate?.id,
    userDetails?.channel_id,
    userDetails?.recruiter_id,
    userDetails?.subhchannel_id,
    userDetails?.x_code,
  ]);

  useEffect(() => {
    // to handle saveAsDraft
    if (isSaveAsDraftSuccess) {
      // clever tap event on save as draft
      eventPushFunction({
        eventName: "Save as a draft",
        data: {
          xcode: userDetails?.x_code || "",
          subuid: userDetails?.recruiter_id || "",
          channel_id: userDetails?.channel_id || "",
          subchannel_id: userDetails?.subhchannel_id || "",
          outreach_type: "WhatsApp",
          step: 2,
          campaign_id: saveAndContinueResponse?.id || "",
          selected_template: templateDetails.selectedTemplate?.id || "",
          media_type: mediaDetails?.mediaType || "",
        },
      });

      window.location.href = `${window.location.protocol}//${window.location.host}${MIDDLEWARE_SLUG}/listing`;
    }

    if (isSaveAsDraftError) {
      setToastValue({
        isToastShown: true,
        toastMessage: getErrorMsg({
          error: saveAsDraftError,
          defaultMsg: "Failed to save as draft. Please ty again.",
        }),
        toastKey: "saveAsDraftError",
        toastSeverity: "error",
      });
    }
  }, [
    isSaveAsDraftError,
    isSaveAsDraftSuccess,
    setToastValue,
    saveAsDraftError,
    saveAndContinueResponse?.id,
    templateDetails.selectedTemplate?.id,
    mediaDetails?.mediaType,
    userDetails?.x_code,
    userDetails?.recruiter_id,
    userDetails?.channel_id,
    userDetails?.subhchannel_id,
  ]);

  useEffect(() => {
    // to update template placeholders details
    if (placeholders && getPlaceholdersStatus === "fulfilled") {
      const selectedTemplateData: ITemplate = placeholders.template;
      let placeholdersData = { ...placeholders } as ITemplatePlaceholderValues;
      delete placeholdersData.template;

      if (selectedTemplateData && templateDetails?.selectedTemplate) {
        const newTemplate = {
          ...templateDetails?.selectedTemplate,
          content: selectedTemplateData?.content || "",
          optionalPlaceholders:
            selectedTemplateData?.optionalPlaceholders || [],
        };

        dispatch(
          campaignComposeActions.setSelectedTemplate({
            selectedTemplate: newTemplate,
          })
        );
        dispatch(
          campaignComposeActions.setTemplatesPlaceholderCache({
            placeholders: placeholdersData,
            template: newTemplate,
          })
        );
      }

      dispatch(
        campaignComposeActions.setTemplatePlaceholders({
          placeholders: placeholdersData,
        })
      );
    }
  }, [dispatch, getPlaceholdersStatus, placeholders]);

  return (
    <Accordion
      title="Compose"
      isEditMode={isEditMode}
      stepsCompleted={stepsCompleted}
      handleEditClick={handleEditClick}
      stepNumber={stepNumber}
    >
      <Box sx={{ my: 4, backgroundColor: COLOR_MAP.GREY_9 }}>
        <Grid container spacing={2}>
          {/* Left Section */}
          <Grid item xs={3}>
            <LeftSection />
          </Grid>

          {/* Middle Section  */}
          <Grid item xs={5.5} lg={6}>
            <MiddleSection
              mediaDetails={{
                handleUpdateMediaDetails,
                addMediaFlag: mediaDetails.addMediaFlag,
                mediaType: mediaDetails.mediaType,
                uploadedFile: mediaDetails.uploadedFile,
                fileName: mediaDetails.mediaFileName,
                fileSize: mediaDetails.mediaFileSize,
                error: errors.addMedia || "",
                handleErrors,
              }}
              senderDetails={senderDetails || defaultSenderDetails}
              templateDetails={{
                handleUpdateSelectedTemplate,
                selectedTemplate: templateDetails?.selectedTemplate,
                templates: templateDetails?.templates || [],
                fetchStatus: templateDetails?.fetchStatus || "idle",
              }}
              placeholdersDetails={templatePlaceholders}
              placeholderFetchStatusDetails={{
                status: getPlaceholdersStatus,
                error: getPlaceholdersError,
              }}
            />
          </Grid>

          {/* Right Section  */}
          <Grid item xs={3.5} lg={3}>
            <RightSection
              message={
                getPlaceholdersStatus === "pending" ||
                getPlaceholdersStatus === "rejected"
                  ? ""
                  : templateDetails?.selectedTemplate?.content || ""
              }
              mediaFile={mediaDetails?.uploadedFile || null}
              mediaType={mediaDetails?.mediaType || "image"}
              mediaFileName={mediaDetails?.mediaFileName || ""}
              mediaFileSize={mediaDetails?.mediaFileSize || ""}
              senderDetails={senderDetails || defaultSenderDetails}
              placeholders={
                getPlaceholdersStatus === "pending" ? {} : templatePlaceholders
              }
              showMedia={mediaDetails.addMediaFlag}
            />
          </Grid>
        </Grid>
      </Box>

      <Divider />
      <CampaignSetupButton
        stepNumber={stepNumber}
        faqData={faqDetails}
        handleSaveAsDraft={handleSaveAsDraft}
        handleStartCampaign={handleStartCampaign}
      />

      {openPlaceholdersDialog && (
        <PlaceholdersDialog
          open={openPlaceholdersDialog}
          handleClose={handleClosePlaceholdersDialog}
        />
      )}
    </Accordion>
  );
};

export default WhatsAppCompose;
